/**
 * Treatment Record — Invoice drawer (PRD.182).
 *
 * Right drawer that hosts the existing InvoicesTab scoped to the resolved
 * encounter for the selected date. The header includes a `DateChipPopover`
 * so the user can change the target date inline; switching dates re-resolves
 * the underlying Visit Note via `ensureEncounterForDate`.
 *
 * Drawer width follows the configured Treatment Record drawer width preset.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { useAuth } from '@helios/auth-firebase-web';
import { useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import { ensureEncounterForDate } from '../../../services/encounter-service';
import { InvoicesTab } from '../consultation/InvoicesTab';
import { todayLocalISO } from './DateChipPopover';
import {
  qEncounterById,
  qEncountersByPatient,
  qPatientById,
  type PatientDetailRow,
  type PatientEncounterRow,
  type TreatmentRecordDrawerWidth,
} from '@helios/data-access';
import { DRAWER_WIDTH_BY_PRESET } from './TreatmentChartingFilesDrawer';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  initialEncounterId: string | null;
  initialDate: string | null;
  drawerWidth: TreatmentRecordDrawerWidth;
  /**
   * Drawer header title. Defaults to "Billing" because the surface
   * includes invoice details + inline Payments + Adjustments sections,
   * so "Add Invoice" no longer captures everything inside.
   */
  title?: string;
  /**
   * Preselect a specific invoice when the drawer opens (e.g. the Pharmacy
   * queue's "open Billing on this invoice" action). Forwarded to the shared
   * InvoicesTab via BillingInvoicesPanel.
   */
  initialInvoiceId?: string;
}

export function TreatmentInvoiceDrawer({
  open,
  onOpenChange,
  patientId,
  initialEncounterId,
  initialDate,
  drawerWidth,
  title = 'Billing',
  initialInvoiceId,
}: Props) {
  const brandTokens = useBrandTokens();

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      portalStyle={brandTokens as CSSProperties}
      width={DRAWER_WIDTH_BY_PRESET[drawerWidth]}
    >
      <BillingInvoicesPanel
        active={open}
        patientId={patientId}
        initialEncounterId={initialEncounterId}
        initialDate={initialDate}
        initialInvoiceId={initialInvoiceId}
      />
    </Drawer>
  );
}

/**
 * The Billing body — the VisitDateNav header + the (two-pane) InvoicesTab —
 * shared so any host drawer renders the exact same invoice/payment experience.
 * Used by the Billing drawer (Queue Pay / Treatment Record) and the PRD.288
 * Initial Fee wizard's payment step, so they stay pixel-consistent.
 *
 * `active` mirrors the host drawer's open state: it re-seeds the date scope each
 * time the surface is (re)shown. `initialInvoiceId` preselects a specific
 * invoice (the Initial Fee flow passes the consultation invoice it just
 * created); Billing leaves it undefined for its default selection.
 */
export function BillingInvoicesPanel({
  active,
  patientId,
  initialEncounterId,
  initialDate,
  initialInvoiceId,
  autoAddPrescriptionIds,
  pharmacyPrescriptionEncounterId,
}: {
  active: boolean;
  patientId: string;
  initialEncounterId: string | null;
  initialDate: string | null;
  initialInvoiceId?: string;
  /** PRD.291 — forwarded to InvoicesTab: create a fresh invoice + add these
   * prescription lines (pharmacy "Today's Prescriptions → Create Invoice"). */
  autoAddPrescriptionIds?: string[];
  /** PRD.292 — forwarded to InvoicesTab: make the "Add Items from Prescription"
   * picker surface this encounter's lines (closed-encounter-safe). */
  pharmacyPrescriptionEncounterId?: string;
}) {
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';

  // Resolve the patient's name so newly created invoices snapshot it. Without
  // this, InvoicesTab's create path stores an empty patient_name (the panel
  // only receives an id), which then reads back blank in downstream views.
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(qPatientById(patientId));
  const patientName = patientRows[0]?.full_name ?? '';

  // PRD.252 follow-up — defaults to "All" (every invoice for the patient).
  // Picking a date scopes the list to that visit's encounter; "All" shows
  // everything. New invoices default to Today (handled by InvoicesTab's create
  // fallback when no encounter is scoped).
  const [mode, setMode] = useState<'all' | 'date'>('all');
  const [encounterId, setEncounterId] = useState<string | null>(initialEncounterId);
  const [date, setDate] = useState<string>(initialDate ?? todayLocalISO());
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    if (active) {
      setMode('all');
      setEncounterId(initialEncounterId);
      setDate(initialDate ?? todayLocalISO());
    }
  }, [active, initialEncounterId, initialDate]);

  // null → "All" (no date scope). A date → scope to that day's encounter.
  const handleSelect = async (sel: string | null) => {
    if (sel === null) {
      setMode('all');
      return;
    }
    setMode('date');
    setDate(sel);
    if (!db || !orgId || !patientId) return;
    setResolving(true);
    try {
      const { encounterId: newEncounterId } = await ensureEncounterForDate({
        db,
        orgId,
        patientId,
        date: sel,
      });
      setEncounterId(newEncounterId);
    } catch (err) {
      console.error('[BillingInvoicesPanel] resolve encounter:', err);
    } finally {
      setResolving(false);
    }
  };

  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncounterById(encounterId ?? ''),
  );
  const visitId = encounterRows[0]?.visit_id ?? undefined;
  const scopedEncounterId = mode === 'date' ? encounterId : null;
  // "All" mode has nothing to resolve; date mode waits for the encounter.
  const ready = mode === 'all' || (!!encounterId && !resolving);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <VisitDateNav
        patientId={patientId}
        selected={mode === 'all' ? null : date}
        onSelect={handleSelect}
      />
      {!ready ? (
        <div
          style={{
            padding: 'var(--sp-6)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {resolving ? 'Switching date…' : 'Loading…'}
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 'var(--sp-5)' }}>
          <InvoicesTab
            key={scopedEncounterId ?? 'all'}
            patientId={patientId}
            patientName={patientName}
            encounterId={scopedEncounterId ?? undefined}
            visitId={mode === 'date' ? (visitId ?? undefined) : undefined}
            // Even in "All" mode (no list scope) we still resolve a real
            // encounter for the date, so anchor any newly created invoice to
            // it + its visit. Without this a Queue Pay invoice is orphaned
            // (encounter_id/visit_id null) and never reaches the visit-scoped
            // Queue Dispense readiness query.
            createEncounterId={encounterId ?? undefined}
            createVisitId={visitId ?? undefined}
            initialInvoiceId={initialInvoiceId}
            autoAddPrescriptionIds={autoAddPrescriptionIds}
            pharmacyPrescriptionEncounterId={pharmacyPrescriptionEncounterId}
            // PRD.182: in Treatment Record Workspace mode, every entered
            // invoice item should land in the treatment record by default.
            defaultAddToTreatment
          />
        </div>
      )}
    </div>
  );
}

// ─── Visit-date navigation row ───
//
// Quick pills for the patient's visit dates (Today + each encounter date) so
// staff can jump between dated invoice sets, with a native date picker pinned
// to the right for any other date.

function pillLabel(iso: string): string {
  if (iso === todayLocalISO()) return 'Today';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

function VisitDateNav({
  patientId,
  selected,
  onSelect,
}: {
  patientId: string;
  /** Active date (YYYY-MM-DD), or null for "All". */
  selected: string | null;
  onSelect: (sel: string | null) => void;
}) {
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );

  // Distinct visit dates (excluding today, which gets its own fixed pill),
  // newest first. The currently-selected date is pinned to the front so it
  // always stays visible even when older dates collapse into the dropdown.
  const visitDates = useMemo(() => {
    const today = todayLocalISO();
    const seen = new Set<string>([today]);
    const visits: string[] = [];
    for (const e of encounterRows) {
      const iso = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!iso || seen.has(iso)) continue;
      seen.add(iso);
      visits.push(iso);
    }
    if (selected && !seen.has(selected)) visits.push(selected);
    visits.sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    if (selected && selected !== today && visits.includes(selected)) {
      return [selected, ...visits.filter((d) => d !== selected)];
    }
    return visits;
  }, [encounterRows, selected]);

  // Measure the pills track so we only render as many date pills as fit; the
  // rest collapse into a "N more ▾" dropdown.
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setTrackWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { visible, hidden } = useMemo(() => {
    // Rough width estimate per pill (px) — avoids a hidden measuring pass and a
    // slight under-fit just collapses one extra date into the dropdown.
    const GAP = 6;
    const est = (label: string) => label.length * 7 + 26;
    const budget =
      (trackWidth || Number.MAX_SAFE_INTEGER) - (est('All') + GAP) - (est('Today') + GAP);
    const reserveMore = est('00 more ▾') + GAP;
    const vis: string[] = [];
    let used = 0;
    for (let i = 0; i < visitDates.length; i++) {
      const w = est(pillLabel(visitDates[i])) + GAP;
      const needMore = i < visitDates.length - 1 ? reserveMore : 0;
      if (used + w + needMore > budget) {
        return { visible: vis, hidden: visitDates.slice(i) };
      }
      used += w;
      vis.push(visitDates[i]);
    }
    return { visible: vis, hidden: [] as string[] };
  }, [trackWidth, visitDates]);

  const today = todayLocalISO();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        padding: '8px var(--sp-5)',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface)',
      }}
    >
      <div
        ref={trackRef}
        style={{ display: 'flex', gap: 6, flex: 1, minWidth: 0, overflow: 'hidden' }}
      >
        {/* "All" — the default: every invoice for the patient, no date scope. */}
        <button type="button" onClick={() => onSelect(null)} style={pillStyle(selected === null)}>
          All
        </button>
        <button type="button" onClick={() => onSelect(today)} style={pillStyle(selected === today)}>
          Today
        </button>
        {visible.map((iso) => (
          <button
            key={iso}
            type="button"
            onClick={() => onSelect(iso)}
            style={pillStyle(iso === selected)}
          >
            {pillLabel(iso)}
          </button>
        ))}
        {hidden.length > 0 && (
          <MoreDatesDropdown dates={hidden} selected={selected} onSelect={onSelect} />
        )}
      </div>
      <input
        type="date"
        value={selected ?? ''}
        onChange={(e) => onSelect(e.target.value || null)}
        title="Pick a date"
        style={{
          flexShrink: 0,
          height: 32,
          padding: '0 var(--sp-2)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-input)',
          background: 'var(--input-bg)',
          color: 'var(--input-text)',
          fontSize: 'var(--font-size-sm)',
          fontFamily: 'inherit',
          outline: 'none',
        }}
      />
    </div>
  );
}

function pillStyle(active: boolean): CSSProperties {
  return {
    flexShrink: 0,
    height: 32,
    padding: '0 12px',
    borderRadius: 'var(--radius-full)',
    border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
    background: active ? 'var(--bg-primary-subtle)' : 'var(--bg-surface)',
    color: active ? 'var(--color-primary)' : 'var(--text-muted)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };
}

// Overflow dropdown for visit dates that don't fit on the pills track.
function MoreDatesDropdown({
  dates,
  selected,
  onSelect,
}: {
  dates: string[];
  selected: string | null;
  onSelect: (iso: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const activeHidden = !!selected && dates.includes(selected);

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button type="button" onClick={() => setOpen((v) => !v)} style={pillStyle(activeHidden)}>
        {activeHidden ? pillLabel(selected!) : `${dates.length} more`} ▾
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 1000,
            minWidth: 180,
            maxHeight: 280,
            overflowY: 'auto',
            padding: 4,
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {dates.map((iso) => {
            const active = iso === selected;
            return (
              <button
                key={iso}
                type="button"
                onClick={() => {
                  onSelect(iso);
                  setOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '8px 10px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: active ? 'var(--bg-primary-subtle)' : 'transparent',
                  color: active ? 'var(--color-primary)' : 'var(--text)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: active ? 600 : 500,
                  textAlign: 'left',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <span>{pillLabel(iso)}</span>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{iso}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
